import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

/**
 * One block of the lead's thread: either a message of the lead's own outreach, or a run of
 * consecutive messages that happened on a colleague's account (PREC-3911).
 */
export type ThreadSegment =
	| { kind: "own"; conversation: Conversation; index: number }
	| { kind: "other"; key: string; name: string; conversations: Conversation[] };

const conversationOwnerName = (conversation: Conversation): string => {
	const { firstName, lastName } = conversation.user ?? {};
	const name = [firstName, lastName].filter(Boolean).join(" ").trim();

	return name || "a colleague";
};

/**
 * Splits a lead's thread into its own messages and the colleagues' conversations around them.
 *
 * Consecutive foreign messages from the same account collapse into one block, so a
 * fifteen-message side conversation costs the reader one line instead of fifteen bubbles
 * they never wrote. Order is preserved: a colleague's block stays where it happened in time,
 * which is what makes it readable as context for the messages around it.
 *
 * The `index` on an own message is its index in the **original** list, so callers can keep
 * comparing it against positions computed over that list (e.g. the last-received anchor).
 */
export const groupThreadConversations = (
	conversations: Conversation[] | undefined
): ThreadSegment[] => {
	const segments: ThreadSegment[] = [];

	(conversations ?? []).forEach((conversation, index) => {
		if (!conversation.isOtherAccount) {
			segments.push({ kind: "own", conversation, index });

			return;
		}

		const ownerKey = String(conversation.user?.id ?? "unknown");
		const previous = segments.at(-1);

		if (previous?.kind === "other" && previous.key === ownerKey) {
			previous.conversations.push(conversation);

			return;
		}

		segments.push({
			kind: "other",
			key: ownerKey,
			name: conversationOwnerName(conversation),
			conversations: [conversation],
		});
	});

	return segments;
};
